import { useState, useEffect } from 'react';
import get from 'lodash/get';
import {
  useWatch,
  Control,
  FieldValues,
  Path,
  UseFormTrigger,
  useFormState,
  useForm,
} from 'react-hook-form';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  Flex,
  TextInputField,
  Button,
  spaces,
  useBreakpoint,
} from '@electricjs/arc';

import {
  AddressGroupField,
  type Address,
} from '@/components/AddressGroupField';

import CountrySelector from './CountrySelector/CountrySelector';

const AddressLineContainer = styled(Flex)`
  position: relative;
  width: 100%;
`;

export type AddressFormProps<T extends FieldValues = FieldValues> = {
  /** Whether the address fields are required for form validation */
  required?: boolean;
  /** The name prefix for the address form fields */
  name?: string;
  /** React Hook Form control object for managing form state */
  control: Control<T>;
  /** React Hook Form trigger function for manual field validation */
  trigger: UseFormTrigger<T>;
  /** Callback function called when any address field value changes */
  onChange: (field: keyof T, value: string) => void;
  /** Google Maps API key for address search functionality.
   * As the Google Maps script is already loaded in the root file on this repo, the API key is optional.
   * If the component is used in Storybook, it needs to receive a valid key to work as expected. */
  googleMapsApiKey?: string;
  /** Whether to show the address search field above the manual fields */
  showAddressSearch?: boolean;
  /** Label for the address search field */
  addressSearchLabel?: string;
};

const AddressForm = <T extends FieldValues = FieldValues>({
  required = false,
  name = '',
  control,
  trigger,
  onChange,
  googleMapsApiKey = '',
  showAddressSearch = false,
  addressSearchLabel,
}: AddressFormProps<T>) => {
  const { t } = useTranslation('common');
  const [showAddressLine2, setShowAddressLine2] = useState(false);

  // Create a separate form control for the AddressGroupField
  const { control: searchControl, setValue: setSearchValue } = useForm<{
    searchAddress: Address;
  }>({
    defaultValues: {
      searchAddress: {
        streetAddress1: '',
        streetAddress2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      },
    },
  });

  const { lg, md } = useBreakpoint();

  const { touchedFields } = useFormState({ control });
  const watchedValues = useWatch({
    name: `${name}` as Path<T>,
    control,
  });
  const streetAddress2Value = watchedValues.streetAddress2;
  const countryValue = watchedValues.country;

  // Check if any address field has a value
  const hasAnyFieldFilled = Object.values(watchedValues).some(Boolean);
  // Check if any address field has been touched
  // This is to prevent the validation from being triggered when the user starts with an empty address
  const hasTouchedFields = !!Object.keys(get(touchedFields, name) || {}).length;

  // Triggers address validation if the user has cleared out all the fields in the address
  useEffect(() => {
    // Only trigger validation if fields were modified and are now empty
    if (!hasAnyFieldFilled && hasTouchedFields) {
      trigger(name as Path<T>);
    }
  }, [name, trigger, hasAnyFieldFilled, hasTouchedFields]);

  // We set the fields as required if the `required` prop is true or if any field has a value (partially completed address)
  // This is used to show the required asterisk in the address fields, it does not affect the validation
  const isRequired = required || hasAnyFieldFilled;

  // Show address line 2 (initially hidden) if it has a preloaded value
  useEffect(() => {
    if (streetAddress2Value && !showAddressLine2) {
      setShowAddressLine2(true);
    }
  }, [streetAddress2Value, showAddressLine2]);

  // Handle address selection from AddressGroupField
  const handleAddressSelection = (address: Address) => {
    // As the handler is triggered on every input change, when the address is empty
    // it means nothing was selected yet, so we return early.
    if (!Object.values(address).some(Boolean)) return;

    // Update the search field to maintain the selected address value
    setSearchValue('searchAddress', address, { shouldValidate: false });

    onChange(`${name}.streetAddress1`, address.streetAddress1);
    onChange(`${name}.streetAddress2`, address.streetAddress2 || '');
    onChange(`${name}.city`, address.city);
    onChange(`${name}.state`, address.state);
    onChange(`${name}.zip`, address.zip || '');
    onChange(`${name}.country`, address.country || '');

    // Show address line 2 if it has a value
    if (address.streetAddress2 && !showAddressLine2) {
      setShowAddressLine2(true);
    }

    // Trigger validation after populating fields
    trigger(name as Path<T>);
  };

  return (
    <Flex width="100%" vertical>
      {showAddressSearch && (
        <AddressGroupField
          id={`${name}-search`}
          name="searchAddress"
          control={searchControl}
          onChange={handleAddressSelection}
          googleMapsApiKey={googleMapsApiKey}
          addressHelperText={t('addressForm.addressHelperText')}
          hideSuiteField
          label={addressSearchLabel || t('addressForm.searchAddress')}
        />
      )}
      <Flex vertical width="100%">
        <AddressLineContainer>
          <TextInputField
            id="street-address-1"
            name={`${name}.streetAddress1`}
            control={control}
            label={t('addressForm.streetAddress1')}
            required={isRequired}
            width="100%"
          />
        </AddressLineContainer>
        {showAddressLine2 ? (
          <TextInputField
            id="street-address-2"
            name={`${name}.streetAddress2`}
            control={control}
            label={t('addressForm.streetAddress2')}
            width="100%"
          />
        ) : (
          <Button
            id="add-address-line-button"
            variant="text"
            onClick={() => setShowAddressLine2(true)}
            iconBefore="add-circle"
            mb={spaces.space_m}>
            {t('addressForm.addAddressLine')}
          </Button>
        )}
      </Flex>
      <Flex width="100%" columnGap={spaces.space_m} vertical={!md}>
        <Flex flex="1" minWidth={md ? 'auto' : '100%'}>
          <TextInputField
            id="city"
            name={`${name}.city`}
            control={control}
            label={t('addressForm.city')}
            required={isRequired}
          />
        </Flex>
        <Flex
          flex="1"
          columnGap={spaces.space_m}
          minWidth={md ? 'auto' : '100%'}
          vertical={!md}>
          <TextInputField
            id="state"
            name={`${name}.state`}
            control={control}
            label={t('addressForm.state')}
            minWidth={lg ? 'auto' : '100%'}
          />
          <TextInputField
            id="zip"
            name={`${name}.zip`}
            control={control}
            label={t('addressForm.postalCode')}
            required={isRequired}
            minWidth={spaces.space_9xl_rem}
          />
        </Flex>
      </Flex>
      <Flex vertical gap={spaces.space_xs} width="100%">
        <CountrySelector
          value={countryValue}
          onChange={value => {
            onChange?.(`${name}.country`, value);
          }}
          required={isRequired}
          name={`${name}.country`}
          control={control}
        />
      </Flex>
    </Flex>
  );
};

export default AddressForm;
